import React from 'react';
import {
  Box,
  Modal,
  Paper,
  Typography,
  Stack,
  Button,
  Fade,
  Backdrop,
  Divider
} from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { LastOrderResponse } from '../types/voiceOrderingTypes';

interface RepeatOrderModalProps {
  lastOrder: LastOrderResponse | null;
  isOpen: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

export default function RepeatOrderModal({
  lastOrder,
  isOpen,
  onAccept,
  onDecline
}: RepeatOrderModalProps) {
  console.log('🔍 [RepeatOrderModal] Render - isOpen:', isOpen, 'lastOrder:', lastOrder);
  if (!lastOrder) {
    console.log('🔍 [RepeatOrderModal] lastOrder is null, returning null');
    return null;
  }

  const items = lastOrder.orderedProducts || [];
  const total = items.reduce((sum: number, item: any) => {
    const unitPrice = item.unitPrice || item.product?.price || 0;
    return sum + (unitPrice * (item.quantity || 1));
  }, 0);

  return (
    <Modal
      open={isOpen}
      closeAfterTransition
      BackdropComponent={Backdrop}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        zIndex: 9999
      }}
      BackdropProps={{
        timeout: 500
      }}
    >
      <Fade in={isOpen}>
        <Paper
          elevation={24}
          sx={{
            width: '90%',
            maxWidth: 500,
            maxHeight: '90vh',
            overflow: 'auto',
            borderRadius: 4,
            bgcolor: 'white',
            position: 'relative'
          }}
        >
          <Box
            sx={{
              bgcolor: '#1E40AF',
              color: 'white',
              p: 3,
              borderRadius: '16px 16px 0 0',
              textAlign: 'center'
            }}
          >
            <RestoreIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h5" fontWeight="bold">
              Commande précédente trouvée
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, mt: 1 }}>
              Vous avez commandé la dernière fois. Voulez-vous la même commande ?
            </Typography>
          </Box>

          <Box sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Récapitulatif de votre dernière commande :
            </Typography>

            <Stack spacing={2} sx={{ mt: 2 }}>
              {items.map((item: any, idx: number) => (
                <Box key={idx}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body1" fontWeight={600}>
                        {item.product?.name || item.productName || 'Produit'}
                      </Typography>
                      {item.trioSelections && item.trioSelections.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {item.trioSelections.map((s: any) => s.name).join(', ')}
                        </Typography>
                      )}
                    </Box>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        x{item.quantity || 1}
                      </Typography>
                      <Typography variant="body1" fontWeight={700}>
                        {(item.unitPrice || item.product?.price || 0).toFixed(2)}$
                      </Typography>
                    </Stack>
                  </Stack>
                  {idx < items.length - 1 && <Divider sx={{ mt: 1 }} />}
                </Box>
              ))}
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h6" fontWeight={800}>Total</Typography>
              <Typography variant="h6" fontWeight={800} color="#1E40AF">
                {total.toFixed(2)}$
              </Typography>
            </Stack>
          </Box>

          <Box sx={{ p: 3, pt: 0 }}>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                fullWidth
                size="large"
                startIcon={<CloseIcon />}
                onClick={onDecline}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  py: 1.5,
                  borderColor: '#E2E8F0',
                  color: '#64748B',
                  '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' }
                }}
              >
                Non, nouvelle commande
              </Button>
              <Button
                variant="contained"
                fullWidth
                size="large"
                startIcon={<CheckCircleIcon />}
                onClick={onAccept}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  py: 1.5,
                  bgcolor: '#1E40AF',
                  '&:hover': { bgcolor: '#1E3A8A' }
                }}
              >
                Oui, je veux la même
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Fade>
    </Modal>
  );
}
